import Service, { service } from '@ember/service';
import { EnvoyDeviceSDK } from '@envoy/device-sdk';
import type {
  ClientBindingTokenStorage,
  ConnectionState,
  DeviceMessage,
  DeviceSDKConfig,
  IEnvoyDeviceSDK,
} from '@envoy/device-sdk';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import type MetricsService from 'garaje/services/metrics';

const CLIENT_BINDING_TOKEN_KEY = 'envoy:mqtt:clientBindingToken';

interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function createSessionStorageBindingTokenStorage(
  storage: SessionStorageLike = sessionStorage,
): ClientBindingTokenStorage {
  return {
    get: () => storage.getItem(CLIENT_BINDING_TOKEN_KEY),
    set: (token) => {
      if (token === null) {
        storage.removeItem(CLIENT_BINDING_TOKEN_KEY);
      } else {
        storage.setItem(CLIENT_BINDING_TOKEN_KEY, token);
      }
    },
  };
}

export type MqttMessageHandler = (msg: DeviceMessage) => void;
export type MqttReconnectHandler = () => void;

export default class MqttConnectionService extends Service {
  @service declare metrics: MetricsService;

  @tracked connectionState: ConnectionState = { type: 'disconnected' };

  #sdk: IEnvoyDeviceSDK | null = null;
  #generation = 0;
  #messageHandler: MqttMessageHandler | null = null;

  async start(
    context: { locationId?: string; zoneId?: string },
    onMessage: MqttMessageHandler,
    onReconnect?: MqttReconnectHandler,
  ): Promise<void> {
    // Skip the broker connection in test runs. Tests that exercise the dispatcher
    // path use mock-mqtt to stub start/stop and dispatch envelopes synchronously.
    if (config.environment === 'test') return;

    // Tear down any existing connection first (location→zone or zone→location switch)
    await this.stop();

    const generation = ++this.#generation;
    const sdk = this.#createSdk(context);
    this.#sdk = sdk;
    this.#messageHandler = onMessage;

    let hasConnectedOnce = false;
    let lastStateType: ConnectionState['type'] = 'disconnected';

    sdk.on('message', onMessage);
    sdk.on('stateChange', (state: ConnectionState) => {
      if (this.#generation !== generation) return;
      this.connectionState = state;

      if (state.type === 'connected') {
        if (hasConnectedOnce && lastStateType !== 'connected') {
          onReconnect?.();
        }
        hasConnectedOnce = true;
      }
      lastStateType = state.type;
    });
    sdk.on('log', (entry) => {
      if (entry.level === 'error') {
        console.error('[MQTT]', entry.message, entry.attributes); // eslint-disable-line no-console
      }
    });

    try {
      await sdk.start();
    } catch (e) {
      console.error('[MQTT] Failed to start:', e); // eslint-disable-line no-console
      this.metrics.logMonitorEvent('MQTT_START_ERROR', { error: (e as Error).message });
    }
  }

  async stop(): Promise<void> {
    const sdk = this.#sdk;
    if (!sdk) return;

    // Bump generation so any in-flight stateChange callbacks are ignored
    this.#generation++;

    if (this.#messageHandler) {
      sdk.off('message', this.#messageHandler);
      this.#messageHandler = null;
    }

    this.#sdk = null;

    try {
      await sdk.stop();
    } catch (e) {
      console.warn('[MQTT] Error during stop:', e); // eslint-disable-line no-console
    }
  }

  get isConnected(): boolean {
    return this.#sdk?.currentState.type === 'connected';
  }

  willDestroy(): void {
    super.willDestroy();
    void this.stop();
  }

  #createSdk(context: { locationId?: string; zoneId?: string }): IEnvoyDeviceSDK {
    const sdkConfig: DeviceSDKConfig = {
      baseURL: config.devicesHost,
      // 'garaje' is supported by the devices service but not yet in the SDK's Product type
      product: 'garaje' as DeviceSDKConfig['product'],
      telemetryProvider: () => ({
        location_id: context.locationId,
        zone_id: context.zoneId,
      }),
      consoleLogging: { prefix: '[EnvoyDeviceSDK:garaje]' },
      clientBindingTokenStorage: createSessionStorageBindingTokenStorage(),
    };

    return new EnvoyDeviceSDK(sdkConfig) as IEnvoyDeviceSDK;
  }
}

declare module '@ember/service' {
  interface Registry {
    'mqtt-connection': MqttConnectionService;
  }
}
